import { useCallback } from 'react';
import { useToken } from '../contexts/TokenContext';

interface LimitErrorResponse {
  success: false;
  code?: string;
  message: string;
  balance?: number;
  freeRemaining?: number;
}

/**
 * Hook to handle API errors and automatically trigger token purchase modal when tokens are exhausted
 * 
 * Usage:
 * ```
 * const { handleTokenError, fetchWithTokenCheck } = useTokenCheck();
 * 
 * // Option 1: Check response automatically
 * const response = await fetch(...);
 * const data = await response.json();
 * const wasTokenError = handleTokenError(response, data);
 * if (wasTokenError) return; // Purchase modal will be shown
 * 
 * // Option 2: Use wrapper function
 * const result = await fetchWithTokenCheck(url, options);
 * if (!result) return; // Token error encountered
 * const { response, data } = result;
 * ```
 */
export const useTokenCheck = () => {
  const { openPurchaseModal, refreshBalance } = useToken();

  /**
   * Check if an API response is a token exhaustion error and automatically trigger purchase modal
   * Returns true if it was a token error, false otherwise
   */
  const handleTokenError = useCallback((response: Response, data: any): boolean => {
    // Check if it's a 402 (Payment Required) with insufficient tokens
    if (response.status === 402 && data && !data.success) {
      const code = data.code || '';
      const message = data.message || '';
      
      const isTokenError = 
        code === 'INSUFFICIENT_TOKENS' ||
        message.toLowerCase().includes('insufficient tokens') ||
        message.toLowerCase().includes('out of tokens') ||
        message.toLowerCase().includes('purchase more tokens');

      if (isTokenError) {
        openPurchaseModal();
        return true;
      }
    }

    return false;
  }, [openPurchaseModal]);

  /**
   * Wrapper for fetch that automatically handles token errors
   * Returns null if a token error was encountered (purchase modal shown)
   * Otherwise returns { response, data }
   */
  const fetchWithTokenCheck = useCallback(async (
    url: string, 
    options?: RequestInit
  ): Promise<{ response: Response; data: any } | null> => {
    const response = await fetch(url, options);
    const data = await response.json();
    
    const wasTokenError = handleTokenError(response, data);
    if (wasTokenError) {
      return null;
    }

    // Update balance from response header if available
    const balanceHeader = response.headers.get('X-Token-Balance');
    if (balanceHeader) {
      // Balance will be updated via WebSocket, but we can trigger a refresh if needed
      // For now, we'll rely on WebSocket updates
    }

    return { response, data };
  }, [handleTokenError]);

  return {
    handleTokenError,
    fetchWithTokenCheck,
    refreshBalance,
  };
};
